import { useRef } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound, useBlocker, useNavigate } from '@tanstack/react-router'
import { AlertCircleIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  DelegationEditor,
  DelegationIssues,
  useDelegation,
} from '#admin/features/registrations/delegation-editor.tsx'
import { useAddParticipants } from '#admin/features/registrations/mutations.ts'
import { registrationQuery } from '#admin/features/registrations/queries.ts'
import { asRegistrationForm } from '#admin/features/registration-form/api.ts'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import { toastError } from '#admin/lib/toast-error.ts'
import { AddParticipantsSkeleton } from '#admin/features/registrations/registration-skeletons.tsx'

/** The delegate's replace caps a delegation here; the API refuses past it. */
const MAX_PARTICIPANTS = 200

/**
 * `/ticketing/registrations/$registrationId/add-participants` — more people on a draft, after the ones
 * already there.
 *
 * The walk-in page's cards (`DelegationEditor`), without the contact: the registration already has
 * one. Only types on sale in its currency are offered, since the API refuses the rest. A sibling
 * of the detail page under the registration's layout, not a child of it: this is its own page, not
 * a panel of that one.
 */
export const Route = createFileRoute(
  '/_authenticated/ticketing/registrations/$registrationId/add-participants',
)({
  pendingComponent: AddParticipantsSkeleton,
  staticData: { crumb: 'Add participants' },
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'ticket.manage')) throw notFound()
  },
  loader: async ({ context, params }) => {
    try {
      await Promise.all([
        context.queryClient.ensureQueryData(registrationQuery(params.registrationId)),
        context.queryClient.ensureQueryData(ticketTypesQuery()),
        context.queryClient.ensureQueryData(formSettingsQuery()),
      ])
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: AddParticipantsPage,
})

function AddParticipantsPage() {
  const { registrationId: id } = Route.useParams()
  const navigate = useNavigate()
  const { data: registration } = useSuspenseQuery(registrationQuery(id))
  const { data } = useSuspenseQuery(ticketTypesQuery())
  const { data: settings } = useSuspenseQuery(formSettingsQuery())
  const form = asRegistrationForm(settings)
  const delegation = useDelegation(form)

  const types = data.items.filter(
    (type) => type.isActive && type.currency === registration.currency,
  )
  const typeById = new Map(data.items.map((type) => [type.id, type]))
  const room = MAX_PARTICIPANTS - registration.participants.length
  const back = () =>
    void navigate({
      to: '/ticketing/registrations/$registrationId',
      params: { registrationId: id },
    })

  // Set just before the redirect a successful add makes, so the leave guard lets it through.
  const added = useRef(false)
  const add = useAddParticipants(() => {
    added.current = true
    back()
  })
  const blocker = useBlocker({
    shouldBlockFn: () => delegation.touched && !added.current,
    enableBeforeUnload: () => delegation.touched && !added.current,
    withResolver: true,
  })

  const submit = () => {
    if (!delegation.validate()) return
    add.mutate(
      { id, form, participants: delegation.participants },
      {
        onError: (error) => {
          if (!delegation.showIssues(error)) toastError(error)
        },
      },
    )
  }

  const count = delegation.rows.length
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Add participants"
        description={`${registration.contactName} · ${registration.participants.length} already on it. Added after them, and the registration is repriced.`}
      />

      {registration.status === 'draft' && room > 0 ? (
        <form
          noValidate
          className="flex max-w-3xl flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault()
            if (!add.isPending) submit()
          }}
        >
          <DelegationIssues delegation={delegation} />
          <DelegationEditor
            delegation={delegation}
            form={form}
            types={types}
            typeById={typeById}
            max={room}
            title="New participants"
          />
          <Button type="submit" className="self-start" disabled={add.isPending}>
            {add.isPending
              ? 'Adding…'
              : count === 1
                ? 'Add 1 participant'
                : `Add ${count} participants`}
          </Button>
        </form>
      ) : (
        <Alert className="max-w-3xl">
          <AlertCircleIcon />
          <AlertTitle>
            {registration.status === 'draft'
              ? `This draft already has ${MAX_PARTICIPANTS} participants`
              : 'Only a draft takes new participants'}
          </AlertTitle>
          <AlertDescription>
            {registration.status === 'draft'
              ? 'Open another registration for the rest of the group.'
              : 'Once submitted, another person changes what is owed — cancel and rebook instead.'}
          </AlertDescription>
        </Alert>
      )}

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => (open ? undefined : blocker.reset?.())}
        title="Leave without adding them?"
        description="What you typed on this page has not been saved."
        confirmLabel="Leave"
        destructive
        onConfirm={() => blocker.proceed?.()}
      />
    </div>
  )
}
